import { CoordinationCard } from '../components/CoordinationCard';
import { useActiveLeaderCount, useCoordinations, useMinistriesWithLeaders } from '../hooks/useOrg';
import { EmptyState } from '@/shared/components/EmptyState';
import { LoadingState } from '@/shared/components/LoadingState';
import { PageHeader } from '@/shared/components/PageHeader';
import { messages } from '@/shared/constants/messages';
import { toUserMessage } from '@/shared/lib/errors';
import { formatList, pluralize } from '@/shared/lib/format';

/**
 * "Como a igreja está organizada" (US-09) — e **só isso**.
 *
 * **Uma grade de cards, um por coordenação.** A versão anterior desta tela
 * trazia todas as coordenações ABERTAS, com os ministérios de cada uma
 * listados dentro, e o argumento era aritmético: com quatro coordenações e dez
 * ministérios a página cabia em uma rolagem, então o acordeão só cobraria um
 * clique por nada.
 *
 * A conta mudou. A estrutura real da igreja tem sete coordenações, e a lista
 * aberta virou uma parede em que a pergunta que traz a pessoa aqui se perde no
 * meio do caminho. O que impede a troca de custar informação é o card: ele
 * carrega o que a coordenação faz e quantos ministérios ela tem. ⚠️ **Os NOMES
 * dos ministérios saíram do card em 23/08/2026** — a justificativa está no
 * docblock do `CoordinationCard`, e ela move a resposta "quais são" de vez para
 * a tela de detalhe.
 *
 * ⚠️ **Atividade de projeto saiu inteira desta aba** (ação 6 da ADR-012): os
 * `StatusTiles` do topo e as pills dos cards. A tela é um **diretório**, não um
 * painel — ela responde como a igreja se organiza, e passa a responder só o que
 * sabe. Os números que estavam aqui vinham recortados pela `projects_select`, o
 * que fazia o topo prometer a atividade da filial e entregar a de quem olha.
 * Quem quer atividade tem `/projetos` e o Calendário.
 *
 * ⚠️ **Os três tiles do topo saíram em 23/08/2026** e viraram a linha embaixo
 * do título — "7 coordenações, 12 ministérios e 9 líderes ativos", sem dizer de
 * quem são (o comentário no corpo explica por que a frase não tem moldura). Os números são os mesmos e as consultas também — o que mudou é o
 * peso que eles têm na tela. Três caixas do tamanho de um card, acima da grade
 * de cards, faziam a aba abrir como painel; e nenhum daqueles números é ação:
 * ninguém clica em "12 ministérios", é contexto de uma frase só. Como frase,
 * eles custam uma linha e devolvem a primeira tela inteira para o que a aba
 * responde de verdade.
 *
 * A contagem de líderes continua vindo de uma RPC própria, e continua podendo
 * chegar depois das outras duas: enquanto ela não chega, a frase sai **sem** a
 * terceira parte, nunca com um `0`. Era o mesmo motivo do `—` no tile antigo —
 * "0 líderes" é uma afirmação forte e falsa sobre a igreja.
 *
 * Quem vê o quê continua sendo a RLS: o líder recebe a própria coordenação, o
 * coordenador a dele, o overseer as da filial e o admin todas. A tela é a
 * mesma para os quatro (papel muda o conteúdo, não o mapa).
 */
export function CoordinationsPage() {
  const coordinationsQuery = useCoordinations();
  const ministriesQuery = useMinistriesWithLeaders();
  const leaderCountQuery = useActiveLeaderCount();

  const error = coordinationsQuery.error ?? ministriesQuery.error ?? leaderCountQuery.error;
  if (error) {
    return <EmptyState title="Não foi possível carregar" description={toUserMessage(error)} />;
  }

  // Guarda pela ausência do dado, e não por `isPending`: só esta forma estreita
  // os dois tipos de uma vez, inclusive dentro dos callbacks abaixo.
  //
  // A contagem de líderes fica FORA desta guarda de propósito: ela é a terceira
  // parte de uma frase, não a tela. Esperar por ela seguraria a grade inteira
  // por causa de um inteiro — a frase sai sem essa parte enquanto ela não chega.
  const coordinations = coordinationsQuery.data;
  const ministries = ministriesQuery.data;
  if (!coordinations || !ministries) {
    return <LoadingState label="Carregando as coordenações…" />;
  }

  const leaderCount = leaderCountQuery.data;
  // Os três números, e nenhuma palavra sobre de QUEM eles são. O menu entrega
  // esta aba a overseer e admin (`ability: 'readOwnBranch'`), para quem eles
  // são o total da filial — mas a ROTA continua aberta a todos por link direto,
  // e aí a RLS devolve só a coordenação da pessoa. "Ao seu alcance" seria
  // rebuscado para os dois primeiros; "da filial" seria FALSO para o terceiro.
  // Sem moldura, a frase é verdadeira nos dois casos.
  //
  // "ativos" carrega a regra de `count_active_leaders()` numa palavra só:
  // coordenador e líder no posto HOJE, nunca auxiliar.
  const summary = formatList([
    pluralize(coordinations.length, 'coordenação', 'coordenações'),
    pluralize(ministries.length, 'ministério', 'ministérios'),
    ...(leaderCount === undefined ? [] : [`${pluralize(leaderCount, 'líder', 'líderes')} ativos`]),
  ]);

  return (
    <div className="space-y-8">
      <PageHeader title="Coordenações" description={`${summary}.`} />

      {coordinations.length === 0 ? (
        <EmptyState
          title={messages.empty.coordinations}
          description="Isso costuma significar que seu vínculo ainda não foi cadastrado. Fale com a secretaria da igreja."
        />
      ) : (
        <ul className="stagger grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {coordinations.map((coordination) => (
            <CoordinationCard
              key={coordination.id}
              id={coordination.id}
              name={coordination.name}
              description={coordination.description}
              icon={coordination.icon}
              ministryCount={
                ministries.filter((ministry) => ministry.coordination_id === coordination.id)
                  .length
              }
            />
          ))}
        </ul>
      )}
    </div>
  );
}
